import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { Button, Col, Modal, Nav, Row, Tab } from "react-bootstrap";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import { permissionAllow, ucFirst } from "../../../../util/Helpers";
import ViewKycModalSkeleton from "./ViewKycModalSkeleton";
import Skeleton from "react-loading-skeleton";
import {
    useConfirmKycMutation,
    useDeclineKycMutation,
    useRejectKycMutation,
    useGetUserPendingKycQuery,
    useUpdateUserPendingKycMutation,
    useGetAddressUpdateRequestsQuery
} from "../../../../../redux/apiSlice";
import cogoToast from "cogo-toast";
import { MultiForm, MultiFormTab } from "../../../Common/components/MultiForm";
import { UserInformation } from "./sections/user-information";
import { TrustInformation } from "./sections/trust-information";
import { InvestmentInformation } from "./sections/investment-information";
import RiskProfile from "./sections/risk-profile";
import { Documents } from "./sections/documents";
import { CIPInformation } from "./sections/cip-information";
import { AddressUpdateInformation } from "./sections/address-update";
import { shallowEqual, useSelector } from "react-redux";
import { PermissionType, Permissions } from "../../../../util/Permissions";
import { Form, Formik } from "formik";
import * as Yup from "yup"
import TextInput from "../../../Common/components/text-input";
import { MoreIcon } from "../../../../util/Icons";
import { Dropdown } from "../../../Common/layout/Dropdown";

const ViewKycModal = ({ showKycModal, userGroup, tableRef, userId, onHide, action }) => {
    const adminUser = useSelector(({ auth }) => (auth.user), shallowEqual);

    /* Fetch User PendingKyc */
    const { isFetching, data: user, error, refetch } = useGetUserPendingKycQuery({ userGroup, userId })

    /* Close modal on failed */
    useEffect(() => {
        if (error && !isFetching) {
            onHide()
        }
    }, [isFetching, error, onHide]);

    const [
        confirmUserKYC, // This is the mutation trigger
        {
            isLoading: isConfirming,
            isSuccess: confirmedSuccessfully,
        }, // This is the destructured mutation result
    ] = useConfirmKycMutation()

    /* Close Modal on KYC Approval */
    useEffect(() => {
        if (confirmedSuccessfully) {
            tableRef.current.refresh();
            cogoToast.success("KYC Confirmed Successfully")
            onHide();
        }
    }, [confirmedSuccessfully, onHide, tableRef]);

    const [
        updateUserKyc, // This is the mutation trigger
        {
            isLoading: isUpdating,
            isSuccess: updatedSuccessfully,
        }, // This is the destructured mutation result
    ] = useUpdateUserPendingKycMutation()

    /* Close Modal on KYC Decline */
    useEffect(() => {
        if (updatedSuccessfully) {
            tableRef.current.refresh();
        }
    }, [updatedSuccessfully, onHide, tableRef]);

    const [
        declineUserKYC, // This is the mutation trigger
        {
            isLoading: isDeclining,
            isSuccess: declinedSuccessfully,
        }, // This is the destructured mutation result
    ] = useDeclineKycMutation()

    /* Close Modal on KYC Decline */
    useEffect(() => {
        if (declinedSuccessfully) {
            tableRef.current.refresh();
            cogoToast.success("KYC Declined Successfully")
            onHide();
        }
    }, [declinedSuccessfully, onHide, tableRef]);

    const [
        rejectUserKYC, // This is the mutation trigger
        {
            isLoading: isRejecting,
            isSuccess: rejectedSuccessfully,
        }, // This is the destructured mutation result
    ] = useRejectKycMutation()

    /* Close Modal on KYC Reject */
    useEffect(() => {
        if (rejectedSuccessfully) {
            tableRef.current.refresh();
            cogoToast.success("KYC Rejected Successfully")
            onHide();
        }
    }, [rejectedSuccessfully, onHide, tableRef]);

    return (
        <Modal
            show={showKycModal}
            onHide={onHide}
            size="xl"
            backdrop={isConfirming ? "static" : true}
            keyboard={!isConfirming}
            aria-labelledby="contained-modal-title-vcenter"
        >
            <Modal.Header>
                <Modal.Title id="contained-modal-title-vcenter" className="w-100">
                    <span className={`svg-icon svg-icon-lg svg-icon-primary mr-3`}>
                        <SVG
                            title=""
                            src={toAbsoluteUrl("/media/svg/icons/General/Binocular.svg")}
                        />
                    </span>
                    {
                        user
                            ? `${ucFirst(user?.firstName ?? "")} ${ucFirst(user?.lastName ?? "")}`
                            : <Skeleton height="25px" width="250px" />
                    }

                    <a
                        href={user ? `/users/view/${user?.userId}` : "#"}
                        target={user ? "_blank" : "_self"}
                        className="float-right mr-5 pr-5 small"
                        rel="noreferrer"
                    >

                        {
                            user ?
                                <>
                                    <span className={`svg-icon svg-icon-lg svg-icon-primary mr-3`}>
                                        <SVG
                                            title=""
                                            src={toAbsoluteUrl("/media/svg/icons/General/User.svg")}
                                        />
                                    </span>
                                    <span className="pr-5 align-bottom">
                                        Profile
                                    </span>
                                </>
                                :
                                <Skeleton height="25px" width="250px" />
                        }

                    </a>

                </Modal.Title>
                <button
                    type="button"
                    className="close"
                    onClick={onHide}
                    disabled={isConfirming}
                >
                    <span aria-hidden="true" className="d-block">×</span>
                    <span className="sr-only">Close</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                {
                    (user?.statusComment && user?.status !== "pending" && user?.status !== "verified") &&
                    <div
                        className={`d-flex align-items-center w-100 p-3 mb-3 rounded-sm text-danger bg-light-danger`}
                    >
                        <span className={"flex-fill font-weight-bold"}>
                            {user?.statusComment}
                        </span>
                    </div>
                }
                {
                    (isFetching && !user)
                        ? <ViewKycModalSkeleton />
                        : <ViewKycForm
                            currentAdmin={adminUser}
                            user={user}
                            refresh={refetch}
                            isFetching={isFetching}
                            onConfirm={confirmUserKYC}
                            isConfirming={isConfirming}
                            onDecline={declineUserKYC}
                            isDeclining={isDeclining}
                            onReject={rejectUserKYC}
                            isRejecting={isRejecting}
                            updateKYC={updateUserKyc}
                            isUpdating={isUpdating}
                            onHide={onHide} 
                            userId={userId}
                            userGroup={userGroup}
                        />
                }
            </Modal.Body>
        </Modal>
    );
}

export function ViewKycForm({
    currentAdmin,
    user,
    activeTab,
    refresh,
    isFetching,
    onConfirm,
    isConfirming,
    onDecline,
    isDeclining,
    onReject,
    isRejecting,
    updateKYC,
    isUpdating,
    onHide,
    userId,
    userGroup
    
}) {

    const [showConfirmOrRejectForm, setShowConfirmOrRejectForm] = useState(false)
    const [showConfirmRejection, setShowConfirmRejection] = useState(false)

    const actionVerb = useMemo(() => {
        if (showConfirmOrRejectForm === "confirm") return "Confirming"
        if (showConfirmOrRejectForm === "decline") return "Declining"
        if (showConfirmOrRejectForm === "reject") return "Rejecting"
    }, [showConfirmOrRejectForm])

    const { data: addressUpdates } = useGetAddressUpdateRequestsQuery({ 
        userId: userId, 
        status: 'PENDING' 
    });

    const hasPendingAddressUpdate = useMemo(() => {
        const updates = addressUpdates?.data || addressUpdates?.rows || addressUpdates || [];
        return updates.some(update => update.status === "PENDING");
    }, [addressUpdates]);

    const tabs = [
        { title: "User Information", key: "userInformation", Component: UserInformation, isRequired: true },
        { title: "Trust Information", key: "trustData", Component: TrustInformation, isRequired: false },
        {
            title: "Investment Information",
            key: "investmentInformation",
            Component: InvestmentInformation,
            isRequired: true
        },
        // Include Risk Profile if user is not Nigerian
        ...(user?.nationality !== "NGA") ? [{
            title: "Risk Profile",
            key: "riskProfile",
            Component: RiskProfile,
            isRequired: true,
        }] : [],
        { title: `Documents (${user?.docs?.length})`, key: "docs", Component: Documents, isRequired: true },
        { title: "Address Update", key: "addressUpdate", Component: AddressUpdateInformation, isRequired: hasPendingAddressUpdate },
        { title: `CIP Checks (${user?.cip?.length})`, key: "cip", Component: CIPInformation, isRequired: user?.userGroup === "trove-fn-us" },
    ]

    const isEditable = permissionAllow(currentAdmin, Permissions.PENDING_KYC, PermissionType.UPDATE)


    return (
        <MultiForm>
            {({ isValid, isDirty }) =>
                <Tab.Container id="left-tabs-example" defaultActiveKey={activeTab ? tabs[activeTab].key : tabs[0].key}>
                    <Row style={{ height: "calc(100vh - 220px)" }}>
                        <Col
                            sm={3}
                            className={"d-flex flex-column border-right h-100"}
                        >
                            <div className={"flex-fill overflow-auto"}>
                                <Nav>
                                    {tabs.map(({ key, title, isRequired }) => (
                                        <MultiFormTab key={key} dataKey={key} title={title} isRequired={isRequired} />
                                    ))}
                                </Nav>
                            </div>
                            {
                                (user?.status === "pending" || user?.status === "verified" || user?.status === "failed") &&
                                <div className={"w-100 position-sticky bottom-0 zindex-1"}>
                                    {
                                        isEditable && (
                                            showConfirmOrRejectForm
                                                ? <div className={`py-3 mt-3 rounded-lg bg-light-${showConfirmOrRejectForm === "confirm" ? "primary" : "danger"}`}>
                                                    <Formik
                                                        initialValues={{ reason: "" }}
                                                        validationSchema={
                                                            Yup.object({ reason: Yup.string().required("required") })
                                                        }
                                                        validateOnMount
                                                        onSubmit={(values) => {
                                                            if (showConfirmOrRejectForm === "confirm") {
                                                                onConfirm({
                                                                    userId: user.userId,
                                                                    userGroup: user.userGroup,
                                                                    statusComment: values?.reason,
                                                                    meta: user,
                                                                })
                                                            } else if (showConfirmOrRejectForm === "decline") {
                                                                onDecline({
                                                                    userId: user.userId,
                                                                    userGroup: user.userGroup,
                                                                    statusComment: values?.reason,
                                                                    meta: user,
                                                                })
                                                            } else if (showConfirmOrRejectForm === "reject") {
                                                                onReject({
                                                                    userId: user.userId,
                                                                    userGroup: user.userGroup,
                                                                    statusComment: values?.reason,
                                                                    meta: user,
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        {
                                                            ({ isValid }) => (
                                                                <Form>
                                                                    {
                                                                        showConfirmRejection
                                                                            ? <div className="px-4 mb-8 mt-2">
                                                                                <h4>Are you sure?</h4>
                                                                                <p>Rejecting this KYC would prevent the user from accessing all Trove applications.</p>
                                                                            </div>
                                                                            : <TextInput
                                                                                label={
                                                                                    `Reason for ${actionVerb} KYC*`
                                                                                }
                                                                                name="reason"
                                                                                type="text"
                                                                                className="border-bottom border-bottom-dark border-transparent bg-transparent h-40px w-100"
                                                                                style={{
                                                                                    outline: "none"
                                                                                }}
                                                                            />
                                                                    }
                                                                    <div className={`px-4 outline-none d-flex`}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setShowConfirmOrRejectForm(false)
                                                                                setShowConfirmRejection(false)
                                                                            }}
                                                                            disabled={isConfirming || isDeclining || isRejecting}
                                                                            className="btn btn-outline-dark btn-elevate px-4 flex-fill"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                        {
                                                                            (showConfirmOrRejectForm === "reject" && !showConfirmRejection)
                                                                                ? <button
                                                                                    type="button"
                                                                                    key="reject"
                                                                                    onClick={() => setShowConfirmRejection(true)}
                                                                                    disabled={isConfirming || isDeclining || isRejecting || !isValid}
                                                                                    className="btn btn-dark btn-elevate px-4 ml-3 flex-fill"
                                                                                >
                                                                                    Submit
                                                                                </button>
                                                                                : <button
                                                                                    type="submit"
                                                                                    disabled={isConfirming || isDeclining || isRejecting || !isValid}
                                                                                    className="btn btn-dark btn-elevate px-4 ml-3 flex-fill"
                                                                                >
                                                                                    {showConfirmRejection ? "Proceed" : "Submit"}
                                                                                </button>
                                                                        }

                                                                    </div>
                                                                </Form>
                                                            )
                                                        }
                                                    </Formik>
                                                </div>
                                                : <>
                                                    {
                                                        user?.status === "pending" &&
                                                        <div className="d-flex">
                                                            <Button
                                                                onClick={() => setShowConfirmOrRejectForm("decline")}
                                                                className={`w-100 flex-fill mr-2`}
                                                                variant={"outline-danger"}
                                                                disabled={isDeclining}
                                                            >
                                                                Decline KYC
                                                            </Button>
                                                            <Dropdown>
                                                                <Dropdown.Button>
                                                                    <Button
                                                                        variant={"outline-danger"}
                                                                        disabled={isRejecting}
                                                                    >
                                                                        <MoreIcon />
                                                                    </Button>
                                                                </Dropdown.Button>
                                                                <Dropdown.Menu anchorRight>
                                                                    <div style={{ width: "160px" }} className="rounded-lg bg-white shadow-sm border overflow-hidden">
                                                                        <div
                                                                            onClick={() => setShowConfirmOrRejectForm("reject")}
                                                                            className={`p-3 text-danger text-center bg-hover-light-danger cursor-pointer`}
                                                                        >
                                                                            Reject KYC
                                                                        </div>
                                                                    </div>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </div>
                                                    }
                                                    {!(user?.status === "verified" && user?.kycLevel === 2) && (
                                                        <Button
                                                            onClick={() => {
                                                                if (user?.nationality === "NGA" && user?.status === "pending") {
                                                                    onConfirm({
                                                                        userId: user.userId,
                                                                        userGroup: user.userGroup,
                                                                        statusComment: "Okay",
                                                                        meta: user,
                                                                    });
                                                                } else {
                                                                    setShowConfirmOrRejectForm("confirm");
                                                                }
                                                            }}
                                                            className={`w-100 mt-3`}
                                                            disabled={!isValid || isDirty || isConfirming}
                                                        >
                                                            Confirm KYC
                                                        </Button>
                                                    )}

                                                </>

                                        )
                                    }
                                </div>
                            }
                        </Col>
                        <Col sm={9} className={"overflow-auto h-100"}>
                            <Tab.Content className={"h-100"}>
                                {tabs.map(({ key, title, Component, isRequired }) => (
                                    <Tab.Pane key={key} eventKey={key} className={"h-100"}>
                                        <Component
                                            dataKey={key}
                                            title={title}
                                            initialValues={{...user, userId, userGroup}}
                                            updateKYC={updateKYC}
                                            isUpdating={isUpdating}
                                            isRequired={isRequired}
                                            isEditable={isEditable}
                                            refresh={refresh}
                                            isFetching={isFetching}
                                        />
                                    </Tab.Pane>
                                ))}
                            </Tab.Content>
                        </Col>
                    </Row>
                </Tab.Container>
            }
        </MultiForm>
    )
}

export default ViewKycModal;